'use client';

import { Dialog, DialogProps } from '@/uiComposites/layout/Dialog.client';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { icon } from '@/uiPrimitives/media/icon';
import { ReactNode, useState } from 'react';
import {
	dialogButton,
	dialogHeader,
	factsheetDialog,
	graphSize,
	graphSizeContainer,
} from './Factsheet.css';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { closeIcon } from '@/uiIcons/closeIcon.css';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { searchIcon } from '@/uiIcons/searchIcon.css';
import { pile } from '@/uiPrimitives/layout/pile';
import { gridStack } from '@/uiPrimitives/layout/gridStack';
import { unprintable } from '@/uiPrimitives/base/printable.css';
import { translateInline } from '@/globals/translate/translateInline';

export const FactsheetDialog = ({
	children,
	title,
}: {
	children?: ReactNode;
	title: string;
}) => {
	const [opened, setOpened] = useState(false);
	const [visible, setVisible] = useState(false);

	return (
		<>
			<div className={classes(unprintable, occupy({ blockSize: 'capSize' }))}>
				<button
					aria-label={translateInline({
						en: 'Zoom in on the chart',
						nl: 'Zoom in op de grafiek',
					})}
					className={classes(dialogButton)}
					onClick={() => {
						setOpened(true);
						setVisible(true);
					}}
				>
					<i
						className={classes(
							icon({
								icon: searchIcon,
								fill: 'positiveBlue',
							}),
							occupy({
								blockSize: '1 | h3',
							}),
							box({
								backgroundColor: 'negative',
								padding: '0 | p',
							}),
						)}
					/>
				</button>
			</div>
			{
				<FactsheetDialogPopup
					open={opened && visible}
					onClose={() => {
						if (visible) setOpened(false);
					}}
					onCancel={() => {
						if (visible) setOpened(false);
					}}
				>
					<div
						className={classes(
							box({ blockSize: '100%' }),
							gridStack({
								rowGap: '5 | betweenParagraphs',
								gridTemplateRows: 'auto minmax(0, 1fr)',
							}),
						)}
					>
						<div
							className={classes(
								dialogHeader,
								textStyle({ capSize: '1 | h3' }),
							)}
						>
							<h3
								style={{ gridArea: 'title' }}
								className={classes(
									textBlock({
										fontWeight: 'bold',
										textAlign: 'center',
									}),
								)}
							>
								{title}
							</h3>

							<div
								style={{ gridArea: 'close' }}
								className={classes(occupy({ blockSize: '1 | h3' }))}
							>
								<button
									aria-label={translateInline({
										en: 'Close the chart',
										nl: 'Sluit de grafiek',
									})}
									onClick={() => {
										setOpened(false);
										setVisible(false);
									}}
									className={classes(
										box({
											backgroundColor: 'negativeBlue',
											inlineSize: '4 | betweenListItems',
											blockSize: '4 | betweenListItems',
										}),
										pile({
											justifyItems: 'center',
											alignItems: 'center',
										}),
									)}
								>
									<i
										className={classes(
											icon({
												fill: 'positiveBlue',
												icon: closeIcon,
												inlineSize: '0 | p',
											}),
										)}
									/>
								</button>
							</div>
						</div>

						<div
							className={classes(
								graphSizeContainer,
								pile({ justifyItems: 'center' }),
							)}
						>
							<div className={classes(graphSize)}>{children}</div>
						</div>
					</div>
				</FactsheetDialogPopup>
			}
		</>
	);
};

const FactsheetDialogPopup = ({
	children,
	className,

	...props
}: DialogProps & { classNameInner?: string }) => {
	return (
		<Dialog {...props} className={classes(factsheetDialog, className)}>
			{children}
		</Dialog>
	);
};
